<template>
  <section class="json-manager">
    <h1>JSONファイル管理</h1>
    <div v-if="message" :class="['message', messageType]">
      {{ message }}
    </div>
    <!-- Upload Section -->
    <div v-if="currentView === 'upload'" class="upload-section">
      <h2>JSONファイルをアップロード</h2>
      <div class="upload-form">
        <input 
          type="file" 
          ref="jsonInput" 
          accept=".json"
          @change="handleFileChange"
          hidden
        />
        
        <div 
          class="dropzone"
          @drop="handleDrop"
          @dragover="handleDragover"
          @click="openFileDialog"
        >
          <div v-if="!selectedFile" class="drop-message">
            <i class="icon">📄</i>
            <p>Drop your JSON file here or click to browse</p>
            <small>Supports .json files only</small>
          </div>
          
          <div v-else class="file-preview">
            <i class="icon">📄</i>
            <div class="file-info">
              <strong>{{ selectedFile.name }}</strong>
              <small>{{ formatFileSize(selectedFile.size) }}</small>
            </div>
            <button @click.stop="clearFile" class="clear-btn">✕</button>
          </div>
        </div>
        
        <div class="actions">
          <button 
            @click="uploadFile" 
            :disabled="!selectedFile || uploading"
            class="btn btn-primary"
          >
            {{ uploading ? 'Uploading...' : 'Upload JSON' }}
          </button>
        </div>
      </div>

    </div>
    

    <!-- JSON Viewer Component -->
    <JsonViewer
      v-if="currentView === 'viewer'"
      :json-data="jsonData"
      :file-stats="fileStats"
      :loading="loading"
      :error="error"
      @replace="handleReplace"
      @message="showMessage"
    />
  </section>
</template>

<script lang="ts">
import { UploadJson } from '@/lib/uploadJson';
import { defineComponent } from 'vue';
import JsonViewer from './JsonViewer.vue';

interface FileStats {
  size: number;
  created: string;
  modified: string;
}

export default defineComponent({
  name: 'JsonManager',
  components: {
    JsonViewer,
  },
  data() {
    return {
      currentView: 'upload' as 'upload' | 'viewer',
      selectedFile: null as File | null,
      uploading: false,
      loading: false,
      message: '',
      messageType: 'info' as 'info' | 'success' | 'error',
      jsonData: null as any,
      fileStats: null as FileStats | null,
      error: '',
      manager: new UploadJson(),
    };
  },
  async mounted() {
    await this.checkExistingFile();
  },
  methods: {
    async checkExistingFile() {
      try {
        const result = await this.manager.get();
        if (result) {
          this.jsonData = result.data;
          this.fileStats = result.fileStats;
          this.currentView = 'viewer';
        }
      } catch (error) {
        // No existing file, stay in upload view
        this.currentView = 'upload';
      }
    },
    openFileDialog() {
      (this.$refs.jsonInput as HTMLInputElement).click();
    },
    handleFileChange(event: Event) {
      const target = event.target as HTMLInputElement;
      if (target.files?.length) {
        this.selectFile(target.files[0]);
      }
    },
    handleDrop(event: DragEvent) {
      event.preventDefault();
      if (!event.dataTransfer) return;
      const file = event.dataTransfer.files[0];
      if (file) this.selectFile(file);
    },
    handleDragover(event: Event) {
      event.preventDefault();
    },
    selectFile(file: File) {
      if (!file.name.toLowerCase().endsWith('.json')) {
        this.showMessage('JSONファイルを選択してください', 'error');
        return;
      }
      this.selectedFile = file;
      this.message = '';
    },
    clearFile() {
      this.selectedFile = null;
      (this.$refs.jsonInput as HTMLInputElement).value = '';
    },
    async uploadFile() {
      if (!this.selectedFile) return;

      this.uploading = true;
      this.message = '';

      try {
        const result = await this.manager.upload(this.selectedFile);
        if (result) {
          this.showMessage(result.message, 'success');
          // Load the uploaded file
          await this.loadCurrentFile();
        } else {
          this.showMessage('アップロードに失敗しました', 'error');
        }
      } catch (error) {
        this.showMessage('アップロードに失敗しました。もう一度お試しください。', 'error');
        console.error('Upload error:', error);
      } finally {
        this.uploading = false;
      }
    },
    async loadCurrentFile() {
      this.loading = true;
      this.error = '';
      try {
        const result = await this.manager.get();
        if (result) {
          this.jsonData = result.data;
          this.fileStats = result.fileStats;
          this.currentView = 'viewer';
        } else {
          this.error = 'ファイルを取得できませんでした';
        }
      } catch (error) {
        this.error = 'ファイルの読み込みに失敗しました';
      } finally {
        this.loading = false;
      }
    },
    async handleReplace() {
      // Switch to upload view for replacement
      this.currentView = 'upload';
      this.selectedFile = null;
      this.message = '';
    },
    showMessage(text: string, type: 'info' | 'success' | 'error') {
      this.message = text;
      this.messageType = type;
      setTimeout(() => {
        this.message = '';
      }, 5000);
    },
    formatFileSize(bytes: number): string {
      if (bytes === 0) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    },
  },
});
</script>

<style lang="scss" scoped>
@use './styles/JsonManager' as *;
</style>